import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import 'firebase/firestore'

import { ClientOnly, Layout } from '../components/common'
import { MetaData } from '../components/common/meta'
import DynamicMap3D from '../components/idealline/DynamicMap3D'
import FirebaseProvider, { FirebaseContext } from '../components/idealline/Firebase'
import RawTelemetryAnalytics from '../components/reports/RawTelemetryAnalytics'
import demoReport from '../data/alfano-gabor-demo.json'
import '../styles/alfano-report.css'
const { normalizeAlfanoSampleAnalytics } = require('../utils/sampleRawAnalytics')

const formatLap = milliseconds => {
  const seconds = milliseconds / 1000
  return `${Math.floor(seconds / 60)}:${(seconds % 60).toFixed(3).padStart(6, '0')}`
}

const polyline = (values, width, height, min, max) => values.map((value, index) => {
  const x = (index / Math.max(1, values.length - 1)) * width
  const y = height - ((value - min) / Math.max(1, max - min)) * height
  return `${x.toFixed(1)},${y.toFixed(1)}`
}).join(' ')

const chartRange = values => {
  const minimum = Math.min(...values)
  const maximum = Math.max(...values)
  const padding = Math.max(1, (maximum - minimum) * 0.08)
  return { minimum: minimum - padding, maximum: maximum + padding }
}

const TelemetryChannelChart = ({ trace, corners, activeCorner, channel, label, unit, lapNumber }) => {
  const values = trace.map(point => Number(point[channel]) || 0)
  const { minimum, maximum } = chartRange(values)
  const width = 760
  const height = 156
  const distanceMax = trace[trace.length - 1]?.distanceM || 1
  const display = value => channel === 'rpm' ? Math.round(value).toLocaleString() : value.toFixed(channel === 'gear' ? 0 : 1)

  return (
    <article className={`alfano-report__channel-chart channel-${channel}`}>
      <header><div><span>{label}</span><strong>{display(Math.min(...values))} to {display(Math.max(...values))} {unit}</strong></div><small>Lap {lapNumber} · distance aligned</small></header>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label} across the fastest lap`}>
        {[0, 1, 2, 3].map(line => <line key={line} x1="0" x2={width} y1={line * (height / 3)} y2={line * (height / 3)} className="trace-grid" />)}
        {corners.map(corner => {
          const x = (corner.distanceM / distanceMax) * width
          return <line key={corner.number} x1={x} x2={x} y1="0" y2={height} className={corner.number === activeCorner ? 'chart-corner active' : 'chart-corner'} />
        })}
        <polyline points={polyline(values, width, height, minimum, maximum)} className="channel-line" />
      </svg>
    </article>
  )
}

const CornerSpeedChart = ({ corners, activeCorner, onCorner }) => {
  const maximum = Math.max(...corners.map(corner => Math.max(corner.baselineApexSpeed, corner.bestApexSpeed)))
  return (
    <div className="alfano-report__corner-speed-chart" aria-label="Best mapped speed by corner">
      {corners.map(corner => (
        <button key={corner.number} className={corner.number === activeCorner ? 'active' : ''} onClick={() => onCorner(corner.number)}>
          <span>C{corner.number}</span>
          <span className="corner-speed-bars">
            <i className="baseline" style={{ height: `${(corner.baselineApexSpeed / maximum) * 100}%` }} />
            <i className="best" style={{ height: `${(corner.bestApexSpeed / maximum) * 100}%` }} />
          </span>
          <strong>{corner.bestApexSpeed.toFixed(1)}</strong>
        </button>
      ))}
      <div className="corner-speed-key"><span><i className="baseline" /> Lap 3</span><span><i className="best" /> Best measured</span></div>
    </div>
  )
}

const OfflineTrackMap = ({ points, corners, activeCorner, onCorner }) => {
  const projected = useMemo(() => {
    const lats = points.map(point => point.lat)
    const lngs = points.map(point => point.lng)
    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)
    const minLng = Math.min(...lngs)
    const maxLng = Math.max(...lngs)
    const project = ({ lat, lng }) => ({
      x: 84 + ((lng - minLng) / (maxLng - minLng)) * 820,
      y: 70 + (1 - ((lat - minLat) / (maxLat - minLat))) * 470,
    })
    return { points: points.map(project), corners: corners.map(corner => ({ ...corner, ...project(corner) })) }
  }, [points, corners])

  return (
    <div className="alfano-report__offline-map">
      <svg viewBox="0 0 1000 620" role="img" aria-labelledby="track-title track-description">
        <title id="track-title">3D telemetry track view</title>
        <desc id="track-description">Fastest lap with inferred braking, coast and acceleration phases and eight corner groups.</desc>
        <g className="track-shadow">
          {projected.points.slice(1).map((point, index) => <line key={`shadow-${index}`} x1={projected.points[index].x} y1={projected.points[index].y + 18} x2={point.x} y2={point.y + 18} />)}
        </g>
        <g className="track-line">
          {projected.points.slice(1).map((point, index) => <line key={index} className={`track-${points[index + 1].type}`} x1={projected.points[index].x} y1={projected.points[index].y} x2={point.x} y2={point.y} />)}
        </g>
        {projected.corners.map(corner => (
          <g className={`track-corner${activeCorner === corner.number ? ' active' : ''}`} key={corner.number} transform={`translate(${corner.x} ${corner.y})`} onClick={() => onCorner(corner.number)} role="button" tabIndex="0">
            <circle r="19" />
            <text textAnchor="middle" dy="6">{corner.number}</text>
          </g>
        ))}
      </svg>
      <div className="alfano-report__map-legend" aria-label="Track line legend">
        <span><i className="brake" /> Speed falling</span>
        <span><i className="coast" /> Stable speed</span>
        <span><i className="acc" /> Speed rising</span>
      </div>
      <p className="alfano-report__map-fallback">Local telemetry view · Google 3D activates when the site map key is available.</p>
    </div>
  )
}

const ReportMap = ({ report, activeCorner, onCorner }) => {
  const mapRef = useRef(null)
  const mapKey = process.env.GATSBY_GMAPS_API_KEY
  const activeData = report.corners.find(item => item.number === activeCorner)

  const focusCorner = cornerNumber => {
    onCorner(cornerNumber)
    const corner = report.corners.find(item => item.number === cornerNumber)
    if (!corner || !mapRef.current || !mapRef.current.flyToPoint) return
    const pointIndex = report.points.reduce((best, point, index) => {
      const distance = Math.abs(point.lat - corner.lat) + Math.abs(point.lng - corner.lng)
      return distance < best.distance ? { distance, index } : best
    }, { distance: Infinity, index: 0 }).index
    mapRef.current.flyToPoint(pointIndex)
  }

  const stepCorner = direction => {
    const currentIndex = report.corners.findIndex(item => item.number === activeCorner)
    const nextIndex = (currentIndex + direction + report.corners.length) % report.corners.length
    focusCorner(report.corners[nextIndex].number)
  }

  return (
    <div className="alfano-report__map">
      {mapKey ? (
        <ClientOnly>
          <DynamicMap3D
            ref={mapRef}
            points={report.points}
            lat={report.points[0].lat}
            lng={report.points[0].lng}
            apikey={mapKey}
            zoom={17}
            pointType="coast"
            addPoint={() => {}}
            readOnly
            embedLayout
          />
        </ClientOnly>
      ) : (
        <OfflineTrackMap points={report.points} corners={report.corners} activeCorner={activeCorner} onCorner={focusCorner} />
      )}
      <nav className="alfano-report__corner-nav" aria-label="Corner coaching points">
        {report.corners.map(corner => (
          <button className={activeCorner === corner.number ? 'active' : ''} key={corner.number} onClick={() => focusCorner(corner.number)} aria-label={`Show ${corner.name}`}>
            C{corner.number}
          </button>
        ))}
      </nav>
      {activeData && (
        <aside className="alfano-report__map-insight" aria-live="polite">
          <header>
            <div><span>C{activeData.number}</span><strong>{activeData.name}</strong></div>
            <div><strong>{activeData.bestApexSpeed.toFixed(1)}</strong><small>km/h best mapped speed</small></div>
          </header>
          <p className="alfano-report__map-insight-action">{activeData.suggestion}</p>
          <p className="alfano-report__map-insight-target"><strong>Next run</strong> {activeData.target}</p>
          <nav className="alfano-report__map-coach-nav" aria-label="Move between coaching points">
            <button type="button" className="previous" aria-label="Previous coaching point" onClick={() => stepCorner(-1)} />
            <span>{activeData.number} of {report.corners.length}</span>
            <button type="button" className="next" aria-label="Next coaching point" onClick={() => stepCorner(1)} />
          </nav>
        </aside>
      )}
    </div>
  )
}

const LapChart = ({ report }) => {
  const fastest = report.overview.fastestLapMs
  return (
    <div className="alfano-report__lap-chart">
      {report.laps.map(lap => {
        const delta = lap.lapTimeMs - fastest
        const width = 54 + ((lap.lapTimeMs - fastest) / 4000) * 38
        return (
          <div className={`lap-row${lap.lapNumber === report.overview.fastestLapNumber ? ' fastest' : ''}`} key={lap.lapNumber}>
            <span>Lap {lap.lapNumber}</span>
            <div className="lap-bar-track"><i style={{ width: `${width}%` }} /></div>
            <strong>{formatLap(lap.lapTimeMs)}</strong>
            <small>{delta === 0 ? 'BEST' : `+${(delta / 1000).toFixed(2)}`}</small>
          </div>
        )
      })}
    </div>
  )
}

const AlfanoCoachingReportContent = ({ location, report, isDemo = false }) => {
  const [activeCorner, setActiveCorner] = useState(8)
  const rawAnalytics = useMemo(() => normalizeAlfanoSampleAnalytics(report), [report])

  return (
    <>
      <MetaData
        location={location}
        title={`${isDemo ? 'Alfano Data Analysis Example | KZ2 Coaching Report' : `${report.session.driver} Alfano Data Analysis Report`} | Open Racer`}
        description="Explore Alfano data analysis from a measured Alfano 6 KZ2 session at Kartódromo do Algarve, with GPS line, speed, RPM and corner-by-corner coaching."
      />
      <Layout bodyClass="alfano-report-page">
        <article className="alfano-report">
          <header className="alfano-report__hero">
            <div className="alfano-report__hero-copy">
              <p className="alfano-report__eyebrow"><span>ALFANO DATA ANALYSIS</span> {report.session.track} · {report.session.date}</p>
              <h1>Alfano data analysis: three changes for your next run</h1>
              <p className="alfano-report__lede"><strong>Find the speed you can repeat.</strong> Five measured laps point to one minimum-speed gain, one shorter lift and one gear experiment.</p>
              <div className="alfano-report__session-line">
                <span>{report.session.driver}</span><span>{report.session.vehicle}</span><span>{report.session.logger} · {report.session.sampleRateHz} Hz</span>
              </div>
            </div>
          </header>

          <section className="alfano-report__visual-section" aria-labelledby="line-title">
            <div className="alfano-report__section-heading">
              <div><p className="alfano-report__eyebrow">CORNER COACH</p><h2 id="line-title">See where speed stays or falls away</h2></div>
              <p>Choose any corner to move the map and keep its next-run instruction on screen. On your phone, use the two compact arrows to work through the lap without leaving the map.</p>
            </div>
            <ReportMap report={report} activeCorner={activeCorner} onCorner={setActiveCorner} />
          </section>

          <section className="alfano-report__scoreboard" aria-label="Session summary">
            <div className="primary"><span>Your fastest lap</span><strong>60.560</strong><small>Lap 3</small></div>
            <div><span>Best possible lap</span><strong>60.440</strong><small>Using Alfano partials</small></div>
            <div><span>Top speed</span><strong>145.3</strong><small>km/h</small></div>
            <div><span>Peak RPM</span><strong>14,734</strong><small>rpm</small></div>
            <div><span>Laps compared</span><strong>5</strong><small>valid laps</small></div>
          </section>

          <section className="alfano-report__diagnosis" aria-labelledby="priority-title">
            <div className="alfano-report__section-heading">
              <div><p className="alfano-report__eyebrow">COACHING PRIORITIES</p><h2 id="priority-title">What to change first</h2></div>
              <p>These are ranked by the change you can practise and transfer around the lap, not by corner number or the lowest speed.</p>
            </div>
            <div className="alfano-report__priority-list">
              {report.priorities.map(priority => {
                const corner = report.corners.find(item => item.number === priority.corner)
                return (
                  <article className={`alfano-report__priority${activeCorner === priority.corner ? ' active' : ''}`} key={priority.rank}>
                    <div className="priority-rank"><span>0{priority.rank}</span><small>{priority.focus}</small></div>
                    <div className="priority-body">
                      <div className="priority-title"><div><small>CORNER {priority.corner} · {corner.name}</small><h3>{priority.title}</h3></div><span>{priority.confidence} confidence</span></div>
                      <div className="priority-evidence">
                        <div><h4>What the Alfano measured</h4><p>{priority.measured}</p></div>
                        <div><h4>Why it matters</h4><p>{priority.interpretation}</p></div>
                      </div>
                      <div className="priority-action"><strong>Next run</strong><p>{priority.action}</p><button onClick={() => setActiveCorner(priority.corner)}>Show C{priority.corner} on the map</button></div>
                    </div>
                    <div className="priority-gain"><strong>{priority.gainMs ? (priority.gainMs / 1000).toFixed(3) : 'TEST'}</strong><span>{priority.gainMs ? 'measured seconds' : 'compare next run'}</span></div>
                  </article>
                )
              })}
            </div>
          </section>

          <section className="alfano-report__data-grid">
            <article className="alfano-report__panel">
              <p className="alfano-report__eyebrow">LAP PROGRESSION</p>
              <h2>Fast by lap three. Then less repeatable.</h2>
              <p>Lap 3 is the session reference. Lap 4 confirms the pace within 0.420 s; Lap 5 contains the best final partial but loses time in partial one.</p>
              <LapChart report={report} />
            </article>
            <article className="alfano-report__panel alfano-report__sectors">
              <p className="alfano-report__eyebrow">OFFICIAL PARTIALS</p>
              <h2>Theoretical lap: 60.440</h2>
              <p>The available 0.120 s is not invented by a model. It is Lap 3 partials one and two plus Lap 5 partial three.</p>
              <div className="sector-grid">
                <div><span>P1</span><strong>14.810</strong><small>Lap 3</small></div>
                <div><span>P2</span><strong>26.890</strong><small>Lap 3</small></div>
                <div className="improvable"><span>P3</span><strong>18.740</strong><small>Lap 5 · 0.120 quicker</small></div>
              </div>
            </article>
          </section>

          <section className="alfano-report__trace-section" aria-labelledby="trace-heading">
            <div className="alfano-report__section-heading compact">
              <div><p className="alfano-report__eyebrow">RAW DATA, MADE USEFUL</p><h2 id="trace-heading">Read the lap, not just the lap time</h2></div>
              <p>The selected corner stays highlighted through every chart. Speed shows where momentum changes, RPM tests the gear choice, and gear position explains the power-band drops.</p>
            </div>
            <div className="alfano-report__telemetry-grid">
              <TelemetryChannelChart trace={report.trace} corners={report.corners} activeCorner={activeCorner} channel="speed" label="Speed" unit="km/h" lapNumber={report.overview.fastestLapNumber} />
              <TelemetryChannelChart trace={report.trace} corners={report.corners} activeCorner={activeCorner} channel="rpm" label="Engine speed" unit="rpm" lapNumber={report.overview.fastestLapNumber} />
              <TelemetryChannelChart trace={report.trace} corners={report.corners} activeCorner={activeCorner} channel="gear" label="Gear" unit="" lapNumber={report.overview.fastestLapNumber} />
              <article className="alfano-report__corner-chart-panel">
                <header><div><span>Mapped corner speed</span><strong>Compare the reference with the best measured lap</strong></div><small>Tap a corner to sync the report</small></header>
                <CornerSpeedChart corners={report.corners} activeCorner={activeCorner} onCorner={setActiveCorner} />
              </article>
            </div>
          </section>

          <section className="alfano-report__run-plan" aria-labelledby="run-plan-title">
            <div>
              <p className="alfano-report__eyebrow">ONE SESSION · THREE TESTS</p>
              <h2 id="run-plan-title">What to test next session</h2>
            </div>
            <ol>
              <li><span>01</span><div><strong>Reproduce Lap 5 at Corner 8</strong><p>Target 91 to 92 km/h at the mapped apex without giving away exit speed.</p></div></li>
              <li><span>02</span><div><strong>Shorten the lift at Corner 3</strong><p>Hold the line constant. Change only lift duration so the comparison stays useful.</p></div></li>
              <li><span>03</span><div><strong>Compare the gear choice at Corner 1</strong><p>Compare minimum RPM plus the following 60 metres, rather than minimum RPM in isolation.</p></div></li>
            </ol>
          </section>

          <aside className="alfano-report__method" aria-labelledby="method-title">
            <div><p className="alfano-report__eyebrow">HOW TO READ THIS</p><h2 id="method-title">Measured first. Interpretation second.</h2></div>
            <div>
              <p>{report.method.sectorSource} {report.method.cornerMethod}</p>
              <ul>{report.method.limits.map(limit => <li key={limit}>{limit}</li>)}</ul>
            </div>
          </aside>
        </article>
        <RawTelemetryAnalytics analytics={rawAnalytics} />
      </Layout>
    </>
  )
}

const PrivateReport = ({ location, reportId }) => {
  const auth = useContext(FirebaseContext)
  const [report, setReport] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!auth?.user || auth.user.isAnonymous || !reportId) return
    let active = true
    auth.app.firestore()
      .collection('customers')
      .doc(auth.user.uid)
      .collection('alfano_reports')
      .doc(reportId)
      .get()
      .then(snapshot => {
        if (!active) return
        if (!snapshot.exists || !snapshot.data()?.report_data) throw new Error('This report is not available yet.')
        setReport(snapshot.data().report_data)
      })
      .catch(loadError => {
        if (active) setError(loadError.message || 'This report could not be loaded.')
      })
    return () => { active = false }
  }, [auth?.user?.uid, reportId])

  if (report) return <AlfanoCoachingReportContent location={location} report={report} />
  return (
    <Layout>
      <main className="mx-auto max-w-2xl px-4 py-20 text-center">
        {!auth?.user || auth.user.isAnonymous ? (
          <>
            <h1 className="text-3xl font-bold">Sign in to view your private report</h1>
            <button className="mt-6 rounded-xl bg-[#5159a2] px-6 py-3 font-semibold text-white" onClick={() => auth?.requireAuth?.(() => {})}>Sign in</button>
          </>
        ) : error ? <p role="alert" className="text-red-700">{error}</p> : <p>Loading your report…</p>}
      </main>
    </Layout>
  )
}

const AlfanoCoachingReport = ({ location }) => {
  const requestedId = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('report')
    : new URLSearchParams(location?.search || '').get('report')
  const reportId = requestedId && /^[A-Za-z0-9_-]{1,100}$/.test(requestedId) ? requestedId : null

  if (!requestedId) return <AlfanoCoachingReportContent location={location} report={demoReport} isDemo />
  if (!reportId) return <Layout><p role="alert">Invalid report link.</p></Layout>
  return <ClientOnly><FirebaseProvider><PrivateReport location={location} reportId={reportId} /></FirebaseProvider></ClientOnly>
}

export default AlfanoCoachingReport
